import type { AppThunk } from '../../../store';
import { store } from '../../../store';
import { isMultiOrgFeatureEnabled } from '../../../utils/features';
import {
  loadGlobalTokens,
  saveActiveTenantIdToStorage,
  type GlobalTokens,
} from '../../../utils/tenantSessionStorage';
import {
  persistTenantSessions,
  selectActiveTenantId,
  selectTenantSessionById,
  setActiveTenantId,
  setTenantSession,
  type TenantSession,
} from '.';
import { updateActiveTenantSessionTokens } from './tenantTokens';

export const upsertTenantSession =
  (session: TenantSession): AppThunk<void> =>
  (dispatch, getState) => {
    if (!isMultiOrgFeatureEnabled()) return;

    dispatch(setTenantSession(session));

    dispatch(setActiveTenantId(session.tenantId));
    saveActiveTenantIdToStorage(session.tenantId);

    persistTenantSessions(getState());
  };

/**
 * Mirrors the given global tokens into the active tenant's session.
 *
 * Only safe to call from refresh-token rotation paths (401 interceptors) where
 * `activeTenantId` is already the owner of the refreshed tokens. During login /
 * add-organisation flows the exchange writes globals for the *new* tenant while
 * Redux still points at the *previous* one, so calling this there would
 * overwrite the previous tenant's snapshot with the new tenant's tokens.
 */
export function syncActiveTenantFromGlobalTokens(tokens?: GlobalTokens): boolean {
  const state = store.getState();
  const activeTenantId = selectActiveTenantId(state);
  if (activeTenantId == null) return false;
  const resolved = tokens ?? loadGlobalTokens();
  if (!resolved) return false;
  const expectedUserId = selectTenantSessionById(activeTenantId)(state)?.userId ?? null;
  return updateActiveTenantSessionTokens(activeTenantId, resolved, expectedUserId);
}
